import {
  createAsyncThunk,
  createSlice,
  createSelector,
  createAction,
} from '@reduxjs/toolkit';
import { has, isEmpty, get, orderBy } from 'lodash';
import { selectUserId } from 'state/User/userSlice';
import {
  allUsers,
  allActiveUsers,
} from 'state/UsersManagement/usersManagementSlice';
import { getOriginalProjectData } from 'state/Project/projectSlice';
import {
  TaskDetailType,
  Task_Status,
  NewProject,
  AllUsersType,
  UserAvatars,
  ProjectContent,
  TaskActualHours,
} from 'utils/customTypes';
import { TASK_FIELDS } from 'utils/constants';
import { taskFields } from 'Pages/ProjectPage/tabs/Tasks/helpers/constants';
import singleTaskAPI from './singleTaskAPI';
import { RootState } from 'state/store';
import { isUserStatusDisabled } from 'utils/functions';
import { Comment } from 'utils/types/comments';

interface taskState {
  value: TaskDetailType;
  taskStatus: Task_Status;
  comments: Comment[];
  // The task whose comments are loaded in `comments`. Fulfilled comment
  // actions for any other task are ignored, so a response that resolves
  // after navigating away can't land in the visible list (CGP-215).
  commentsTaskId: string | null;
}

/* ============================= INITIAL STATE ============================== */
const initialState: taskState = {
  value: taskFields,
  taskStatus: 'new',
  comments: [],
  commentsTaskId: null,
};

/* ============================= ACTIONS ============================== */
export const setTaskDetail = createAction<TaskDetailType>(
  'singleTask/SET_TASK_DETAIL'
);

export const addContentFiles = createAction<ProjectContent[]>(
  'singleTask/ADD_CONTENT_FILES'
);

export const setTaskId = createAction<string>('singleTask/SET_TASK_ID');

export const resetTaskComments = createAction('singleTask/RESET_TASK_COMMENTS');

const taskAPI = singleTaskAPI;
/* ============================== REDUX THUNK =============================== */

export const fetchTask = createAsyncThunk(
  'singleTask/FETCH_TASK',
  async (taskId: string) => {
    const response = await taskAPI.fetchTask(taskId);
    return response.data.task;
  }
);

export const fetchTaskComments = createAsyncThunk(
  'singleTask/FETCH_TASK_COMMENTS',
  async (taskId: string) => {
    const response = await taskAPI.fetchTaskComments(taskId);
    return response;
  }
);

export const createTaskComment = createAsyncThunk(
  'singleTask/CREATE_TASK_COMMENT',
  async ({
    taskId,
    content,
    mentions,
  }: {
    taskId: string;
    content: string;
    mentions: string[];
  }) => {
    const response = await taskAPI.createTaskComment(taskId, content, mentions);
    return response;
  }
);

export const updateTaskComment = createAsyncThunk(
  'singleTask/UPDATE_TASK_COMMENT',
  async ({
    commentId,
    taskId,
    content,
    mentions,
  }: {
    commentId: string;
    taskId: string;
    content: string;
    mentions: string[];
  }) => {
    const response = await taskAPI.updateTaskComment(
      commentId,
      taskId,
      content,
      mentions
    );
    return response;
  }
);

export const deleteTaskComment = createAsyncThunk(
  'singleTask/DELETE_TASK_COMMENT',
  async ({ commentId }: { commentId: string }) => {
    const response = await taskAPI.deleteTaskComment(commentId);
    return response;
  }
);

export const fetchTaskAndActualHours = createAsyncThunk(
  'singleTask/FETCH_TASK_AND_ACTUAL_HOURS',
  async (taskId: string) => {
    const [taskResponse, actualHoursResponse] = await Promise.all([
      taskAPI.fetchTask(taskId),
      taskAPI.fetchTaskActualHours(taskId),
    ]);
    return {
      task: taskResponse.data.task,
      actualHours: actualHoursResponse.data.actualHours || [],
    };
  }
);

export const updateTaskActualHours = createAsyncThunk(
  'singleTask/UPDATE_TASK_ACTUAL_HOURS',
  async ({
    taskId,
    newActualHours,
    removedActualHours,
  }: {
    taskId: string;
    newActualHours: TaskActualHours[];
    removedActualHours: string[];
  }) => {
    const response = await taskAPI.updateTaskActualHours(
      taskId,
      newActualHours,
      removedActualHours
    );
    return response.data.updatedActualHours;
  }
);

export const resetTask = createAction('project/RESET_TASK');

export const updateTask = createAsyncThunk(
  'singleTask/UPDATE_TASK',
  async (updateData: { taskId: string; data: TaskDetailType | any }) => {
    let fieldsToUpdate = { ...updateData.data };
    let updatedStatus = null;
    let updatedAssignedUsers = undefined;

    if (has(fieldsToUpdate, TASK_FIELDS.STATUS)) {
      const {
        [TASK_FIELDS.STATUS]: statusUpdate,
        ...restOfFieldsWithoutStatus
      } = fieldsToUpdate;
      updatedStatus = statusUpdate;
      fieldsToUpdate = restOfFieldsWithoutStatus;
    }

    if (has(fieldsToUpdate, TASK_FIELDS.ASSIGNEE_UPDATE)) {
      const {
        [TASK_FIELDS.ASSIGNEE_UPDATE]: assigneeUpdate,
        ...restOfFieldsWithoutAssignees
      } = fieldsToUpdate;
      updatedAssignedUsers = assigneeUpdate;
      fieldsToUpdate = restOfFieldsWithoutAssignees;
    }

    let updatedtask = fieldsToUpdate;
    if (!isEmpty(fieldsToUpdate)) {
      const resp = await taskAPI.updateTask(updateData.taskId, fieldsToUpdate);
      if (resp !== null) {
        updatedtask = {
          ...updatedtask,
          ...resp,
        };
      }
    }

    if (updatedStatus !== null) {
      await taskAPI.updateStatus(updateData.taskId, updatedStatus);
      updatedtask = {
        ...updatedtask,
        [TASK_FIELDS.STATUS]: updatedStatus,
      };
    }

    if (updatedAssignedUsers !== undefined) {
      const response = await taskAPI.updateAssignees(
        updateData.taskId,
        updatedAssignedUsers
      );
      if (response && response.assignedUsers) {
        updatedtask = {
          ...updatedtask,
          [TASK_FIELDS.ASSIGNEE_UPDATE]: response.assignedUsers,
        };
      }
    }

    return { ...updatedtask };
  }
);

export const updateLinkedContent = createAsyncThunk(
  'singleTask/UPDATE_LINKED_CONTENT',
  async (params: { taskId: string; linkedContentFiles: ProjectContent[] }) => {
    const contentFilesIds = params.linkedContentFiles.map(
      (linkedContentFile: ProjectContent) => linkedContentFile.id!
    );
    await taskAPI.updateLinkedContent(params.taskId, contentFilesIds);
    return params.linkedContentFiles;
  }
);

export const updateTaskEnablement = createAsyncThunk(
  'singleTask/UPDATE_TASK_ENABLEMENT',
  async ({ taskId, disabled }: { taskId: string; disabled: boolean }) => {
    await taskAPI.updateEnablement(taskId, disabled);
    return disabled;
  }
);

/* ================================= REDUCER ================================ */
const singleTaskSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateTask.fulfilled, (state, action) => {
        state.value = { ...state.value, ...action.payload };
      })
      .addCase(fetchTask.fulfilled, (state, action) => {
        const { assignedUsers = [], ...remainingTaskData } = action.payload;
        const ownersIds = assignedUsers?.map((owner: any) => owner.id || '');

        state.value = {
          ...remainingTaskData,
          assignedUsers: ownersIds,
          taskActualHours: state.value.taskActualHours,
        };
      })
      .addCase(fetchTaskComments.pending, (state, action) => {
        state.commentsTaskId = action.meta.arg;
      })
      .addCase(fetchTaskComments.fulfilled, (state, action) => {
        if (action.meta.arg !== state.commentsTaskId) {
          return;
        }
        state.comments = action.payload;
      })
      .addCase(resetTaskComments, (state) => {
        state.comments = [];
        state.commentsTaskId = null;
      })
      .addCase(createTaskComment.fulfilled, (state, action) => {
        if (action.meta.arg.taskId !== state.commentsTaskId) {
          return;
        }
        state.comments.unshift(action.payload);
      })
      .addCase(updateTaskComment.fulfilled, (state, action) => {
        if (action.meta.arg.taskId !== state.commentsTaskId) {
          return;
        }
        const updatedCommentIndex = state.comments.findIndex(
          (comment) => comment.id === action.payload.id
        );
        // A missing comment must not be spliced in: with index -1 the tail
        // becomes slice(0) — the whole list — duplicating every comment and
        // inserting the payload as a phantom entry (CGP-215).
        if (updatedCommentIndex === -1) {
          return;
        }
        state.comments = [
          ...state.comments.slice(0, updatedCommentIndex),
          action.payload,
          ...state.comments.slice(updatedCommentIndex + 1),
        ];
      })
      .addCase(deleteTaskComment.fulfilled, (state, action) => {
        state.comments = state.comments.filter(
          (comment) => comment.id !== action.payload
        );
      })
      .addCase(setTaskDetail, (state, action) => {
        state.value = action.payload;
      })
      .addCase(resetTask, (state) => {
        state.value = taskFields;
      })
      .addCase(updateTaskEnablement.fulfilled, (state, action) => {
        state.value = {
          ...state.value,
          disabled: action.payload,
        };
      })
      .addCase(addContentFiles, (state, action) => {
        state.value = {
          ...state.value,
          linkedContent: state.value.linkedContent?.concat(action.payload),
        };
      })
      .addCase(setTaskId, (state, action) => {
        state.value = {
          ...state.value,
          id: action.payload,
        };
      })
      .addCase(updateLinkedContent.fulfilled, (state, action) => {
        state.value = {
          ...state.value,
          linkedContent: action.payload,
        };
      })
      .addCase(fetchTaskAndActualHours.fulfilled, (state, action) => {
        if (!action.payload || !action.payload.task) {
          return;
        }
        const {
          task: { assignedUsers = [], ...task },
          actualHours,
        } = action.payload;
        const ownersIds = assignedUsers?.map((owner: any) => owner.id || '');

        state.value = {
          ...task,
          assignedUsers: ownersIds,
          taskActualHours: orderBy(actualHours, 'actual_hours_date', 'asc'),
        };
      })
      .addCase(updateTaskActualHours.fulfilled, (state, action) => {
        state.value.taskActualHours = action.payload;
      });
  },
});

/* =============================== SELECTORS ================================ */
export const getSingleTaskData = (state: RootState) => state.singleTask.value;
export const getSingleTaskId = (state: RootState) =>
  state.singleTask.value.id || state.taskDetail.currentTask?.id;
export const getTaskComments = (state: RootState) => state.singleTask.comments;
export const getLinkedTaskContentFilesIds = createSelector(
  [getSingleTaskData],
  (taskData: TaskDetailType) =>
    taskData.linkedContent?.map(
      (linkedContent: ProjectContent) => linkedContent.id
    )
);

const getTaskAssignees = createSelector(
  [getSingleTaskData],
  (task: TaskDetailType) => {
    if (!task) {
      return [];
    }
    return task.assignedUsers
      ? task.assignedUsers.map((assignee) => {
          if (typeof assignee === 'string') {
            return assignee;
          } else {
            return assignee.id;
          }
        })
      : [];
  }
);

export const isCurrentUserAssignedToTask = createSelector(
  [getTaskAssignees, selectUserId],
  (assignees: string[], currentUserId: string = '') => {
    return assignees.includes(currentUserId);
  }
);

export const getFormattedTaskAssignees = createSelector(
  [getTaskAssignees, allUsers],
  (assignees: string[], users: AllUsersType[]) => {
    return users
      .filter((user: AllUsersType) => assignees.includes(user.id))
      .map((user: AllUsersType) => ({
        label: `${user.data.firstName} ${user.data.lastName}`,
        avatar: {
          imageSrc: user.avatar_url,
          initial: `${user.data?.firstName?.charAt(
            0
          )}${user.data?.lastName?.charAt(0)}`,
          name: `${user.data?.firstName} ${user.data?.lastName}`,
        },
        disabled: isUserStatusDisabled(user.status),
        value: user.id,
      })) as UserAvatars[];
  }
);

export const getAvailableUsersForTaskAssignees = createSelector(
  [getOriginalProjectData, getTaskAssignees, allActiveUsers],
  (project: NewProject, assignees: string[], users: AllUsersType[]) => {
    const usersList: string[] = [];
    for (let owner of project.owners) {
      if (!assignees.includes(get(owner, 'project_owners.userId'))) {
        usersList.push(get(owner, 'project_owners.userId'));
      }
    }

    if (project.participants) {
      for (let member of project.participants) {
        if (!assignees.includes(get(member, 'project_participants.userId'))) {
          usersList.push(get(member, 'project_participants.userId'));
        }
      }
    }

    if (project.collaborators) {
      for (let collaborator of project.collaborators) {
        if (
          !assignees.includes(get(collaborator, 'project_collaborators.userId'))
        ) {
          usersList.push(get(collaborator, 'project_collaborators.userId'));
        }
      }
    }
    return users
      .filter((user: AllUsersType) => usersList.includes(user.id))
      .map((user: AllUsersType) => ({
        label: `${user.data.firstName} ${user.data.lastName}`,
        avatar: {
          imageSrc: user.avatar_url,
          initial: `${user.data?.firstName?.charAt(
            0
          )}${user.data?.lastName?.charAt(0)}`,
          name: `${user.data?.firstName} ${user.data?.lastName}`,
        },
        disabled: isUserStatusDisabled(user.status),
        value: user.id,
      })) as UserAvatars[];
  }
);

export default singleTaskSlice.reducer;
